import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRight, Search } from 'lucide-react';
import MarketingLayout from '../../components/MarketingLayout';
import { publicAdviserApi, type DirectoryResult } from '../../advisers/api';
import { AdviserCard } from '../../advisers/AdviserProfileView';
import '../../advisers/Advisers.css';

export default function AdviserDirectoryPage() {
  const [params, setParams] = useSearchParams();
  const [result, setResult] = useState<DirectoryResult | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [retry, setRetry] = useState(0);
  const search = params.get('search') || '';
  const industry = params.get('industry') || '';
  const expertise = params.get('expertise') || '';
  const location = params.get('location') || '';
  const page = Math.max(1, Number(params.get('page')) || 1);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    publicAdviserApi
      .list({ search, industry, expertise, location, page, limit: 12 })
      .then((data) => {
        if (active) setResult(data);
      })
      .catch((err) => {
        if (active) setError(err.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [search, industry, expertise, location, page, retry]);
  const changePage = (next: number) => {
    const nextParams = new URLSearchParams(params);
    nextParams.set('page', String(next));
    setParams(nextParams);
  };
  return (
    <MarketingLayout className="adviser-public">
      <section className="adviser-directory-hero public-container">
        <p className="adviser-eyebrow">People who move strategy forward</p>
        <h1>Find your strategic adviser.</h1>
        <p>
          Explore firms that can help you turn your business context into clear priorities and
          practical action.
        </p>
        <Link className="adviser-text-link" to="/adviser">
          Run an advisory firm? Join the programme <ArrowRight size={16} />
        </Link>
      </section>
      <section
        className="public-container adviser-directory-content"
        aria-label="Adviser directory"
      >
        <form
          className="adviser-filter"
          onSubmit={(event) => {
            event.preventDefault();
            const fields = new FormData(event.currentTarget);
            const next = new URLSearchParams();
            fields.forEach((value, key) => {
              if (String(value).trim()) next.set(key, String(value).trim());
            });
            setParams(next);
          }}
        >
          <label>
            Search firms
            <input
              name="search"
              type="search"
              defaultValue={search}
              placeholder="Firm or service"
              key={`search-${search}`}
            />
          </label>
          <label>
            Industry
            <input
              name="industry"
              defaultValue={industry}
              placeholder="e.g. Childcare"
              key={`industry-${industry}`}
            />
          </label>
          <label>
            Expertise
            <input
              name="expertise"
              defaultValue={expertise}
              placeholder="e.g. Growth"
              key={`expertise-${expertise}`}
            />
          </label>
          <label>
            Location
            <input
              name="location"
              defaultValue={location}
              placeholder="City or service area"
              key={`location-${location}`}
            />
          </label>
          <button className="adviser-button" type="submit">
            <Search size={17} />
            Search
          </button>
          {(search || industry || expertise || location) && (
            <button
              className="adviser-button adviser-button-secondary"
              type="button"
              onClick={() => setParams({})}
            >
              Clear
            </button>
          )}
        </form>
        <div aria-live="polite">
          {loading ? (
            <p className="adviser-empty">Finding advisers…</p>
          ) : error ? (
            <div className="adviser-error" role="alert">
              {error}
              <button type="button" onClick={() => setRetry((value) => value + 1)}>
                Try again
              </button>
            </div>
          ) : (
            <>
              <p className="adviser-muted">
                {result?.total || 0} {result?.total === 1 ? 'firm' : 'firms'} found
              </p>
              {result?.advisers?.length ? (
                <div className="adviser-card-grid">
                  {result.advisers.map((adviser) => (
                    <AdviserCard adviser={adviser} key={adviser.slug} />
                  ))}
                </div>
              ) : (
                <div className="adviser-empty">
                  <h2>
                    {search || industry || expertise || location
                      ? 'No firms match these filters yet.'
                      : 'Meet our advisers soon.'}
                  </h2>
                  <p>
                    {search || industry || expertise || location
                      ? 'Try a broader search or clear your filters.'
                      : 'Approved advisory firms will appear here as they publish their profiles.'}
                  </p>
                </div>
              )}
              {result && result.total > result.limit && (
                <nav className="adviser-pagination" aria-label="Directory pages">
                  <button type="button" disabled={page <= 1} onClick={() => changePage(page - 1)}>
                    Previous
                  </button>
                  <span>
                    Page {page} of {Math.ceil(result.total / result.limit)}
                  </span>
                  <button
                    type="button"
                    disabled={page * result.limit >= result.total}
                    onClick={() => changePage(page + 1)}
                  >
                    Next
                  </button>
                </nav>
              )}
            </>
          )}
        </div>
      </section>
    </MarketingLayout>
  );
}
